<template>
  <div class="outer">
    <NxBecomingLwBanner v-if="user && !isNxToLwBannerDismissed" />
    <div class="adminlogin" v-if="user && isAdmin">
      <div>
        Logged In As <strong>{{ user.identity }}</strong>
      </div>
    </div>

    <div class="layout-leftnav">
      <NavLeft
        v-if="showNav && !hideSidebar"
        :loading="menu.loading"
        :back="menu.back"
        :menuTitle="menu.title"
        :menuItems="menu.links"
        :helpItems="helpLinks"
        class="d-none d-lg-flex"
      />
      <div class="right-content">
        <template v-if="showNav">
          <NavTopMobile
            class="top-nav-bar d-lg-none"
            :user="user"
            :client="client"
            :userMenuItems="userMenuItemsMobile"
            :loading="menu.loading"
            :back="menu.back"
            :menuTitle="menu.title"
            :menuItems="menu.links"
            :helpItems="helpLinks"
          />
          <NavTopAuth
            class="top-nav-bar d-none d-lg-block tw-px-8 tw-py-4"
            :client="client"
            :user="user"
            :userMenuItems="userMenuItemsMobile"
            :userLoginableItems="userLoginableItems"
          />
        </template>
        <NavTopAnon class="top-nav-bar" v-else-if="!isLoginPage" />

        <div class="content">
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "vue";
import { canWrite } from "@/utils/useAuth";
import constants from "@/utils/constants";
import ContactSupport from "@/components/smart/modals/ContactSupport";
import Logout from "@/components/smart/modals/Logout";
import NavLeft from "@/components/nav/NavLeft";
import NavLink from "@/classes/NavLink";
import { nameMapper as navLinkNameMapper } from "@/utils/navLinks";
import NavTopAnon from "@/components/nav/NavTopAnon";
import NavTopAuth from "@/components/nav/NavTopAuth";
import NavTopMobile from "@/components/nav/NavTopMobile";
import NxBecomingLwBanner from "@/components/misc/NxBecomingLwBanner.vue";
import { openChat } from "@/utils/chat";
import ReferAFriendPromotionMenuItemAddon from "@/components/misc/ReferAFriendPromotionMenuItemAddon";
import router from "@/router";
import store from "@/store";
import SupportCallModal from "@/components/smart/modals/SupportCall";
import SwitchClient from "@/components/smart/modals/SwitchClient";
import UpdateDefaultClient from "@/components/smart/modals/UpdateDefaultClient";

export default {
  name: "GlobalNav",
  components: {
    NavLeft,
    NavTopAnon,
    NavTopAuth,
    NavTopMobile,
    NxBecomingLwBanner,
  },
  setup() {
    // const enableSneakPeek = false;

    const chatLoading = ref(false);

    const chatIcon = computed(() =>
      chatLoading.value
        ? { default: { icon: "fa-duotone fa-loader", spin: true } }
        : {
            default: { icon: "fa-regular fa-comment" },
            hover: { icon: "fa-solid fa-comment" },
          }
    );

    const client = computed(() => store.state.auth.client);

    const helpLinks = computed(() => [
      new NavLink("Chat", chatIcon.value, () => {
        const matchedRoutes = [...router.currentRoute.value.matched];
        const isCheckout = matchedRoutes.reverse().find((route) => !!route.meta.isCheckout);
        const extraDetails = {
          preselectDepartment:
            isCheckout && store.getters["surveys/requireSurvey"]
              ? store.getters["surveys/getSurveyType"]
              : null,
        };

        chatLoading.value = true;

        openChat(extraDetails).finally(() => (chatLoading.value = false));
      }),
      new NavLink(
        "Email",
        { default: { icon: "fa-regular fa-envelope" }, hover: { icon: "fa-solid fa-envelope" } },
        () => {
          return store.dispatch("modals/open", { template: ContactSupport });
        }
      ),
      new NavLink(
        "Call",
        {
          default: { icon: "fa-regular fa-phone" },
          hover: { icon: "fa-solid fa-phone" },
        },
        () => {
          store.dispatch("modals/open", {
            template: SupportCallModal,
          });
        }
      ),
    ]);

    const hideSidebar = computed(() => store.getters["nav/hideSidebar"]);

    const isAdmin = computed(() => store.getters["auth/isAdmin"]);

    const isLoggedIn = computed(() => store.getters["auth/isLoggedIn"]);

    const isLoginPage = computed(() => router.currentRoute.value.path === "/login");

    const isNxToLwBannerDismissed = computed(() => store.getters["nxToLw/isBannerDismissed"]);

    const menu = computed(() => store.getters["nav/getMenu"]);

    const showNav = computed(
      () => store.getters["maintenance/getMode"] !== constants.MODES.MAINTENANCE && isLoggedIn.value
    );

    const user = computed(() => store.state.auth.user);

    const userLoginableItems = computed(() => store.getters["auth/getLoginableClientNavLinks"]);

    const userMenuItems = computed(() =>
      createNavLinks([
        {
          name: "profile",
          route: "profile",
        },
        {
          name: "account-settings",
          route: "account-settings",
        },
        {
          name: "2fa",
          route: "2fa",
        },
        {
          name: "feedback",
          route: "feedback",
        },
        {
          name: "signout",
          action: logout,
        },
      ])
    );

    /*
      ORIGINAL TOP RIGHT MENU LIST
      kb
      ssh
      api
      2fa
      settings
      refer friend
      team
      addresses
      announcements
      switch
      set default
      feedback
      sign out
    */
    const userMenuItemsMobile = computed(() => {
      const menuItems = [];

      menuItems.push({
        name: "profile",
        route: "profile",
        icon: {
          default: { icon: "fa-regular fa-user" },
          hover: { icon: "fa-solid fa-user" },
        },
      });
      menuItems.push({
        name: "account-settings",
        route: "account-settings",
        icon: {
          default: { icon: "fa-regular fa-gear" },
          hover: { icon: "fa-solid fa-gear" },
        },
      });
      menuItems.push({
        name: "2fa",
        route: "2fa",
        icon: {
          default: { icon: "fa-regular fa-shield-check" },
          hover: { icon: "fa-solid fa-shield-check" },
        },
      });
      menuItems.push({
        name: "ssh-keys",
        route: "ssh-keys",
        icon: {
          default: { icon: "fa-regular fa-key" },
          hover: { icon: "fa-solid fa-key" },
        },
      });
      if (canWrite("users")) {
        menuItems.push({
          name: "team",
          route: "team",
          icon: {
            default: { icon: "fa-regular fa-users" },
            hover: { icon: "fa-solid fa-users" },
          },
        });
      }
      menuItems.push({
        name: "api-tokens",
        route: "api-tokens",
        icon: {
          default: { icon: "fa-regular fa-coin-blank" },
          hover: { icon: "fa-solid fa-coin-blank" },
        },
      });
      menuItems.push({
        name: "refer-a-friend",
        route: "refer-a-friend",
        slot: ReferAFriendPromotionMenuItemAddon,
        icon: {
          default: { icon: "fa-regular fa-gift" },
          hover: { icon: "fa-solid fa-gift" },
        },
      });
      menuItems.push({
        name: "addresses",
        route: "payment-info",
        icon: {
          default: { icon: "fa-regular fa-address-card" },
          hover: { icon: "fa-solid fa-address-card" },
        },
      });

      let menuLinks = createNavLinks(menuItems);

      if (userLoginableItems.value && userLoginableItems.value.length > 1) {
        const loginableLinks = createNavLinks([
          {
            name: "switch-client",
            action: () => {
              store.dispatch("modals/open", {
                template: SwitchClient,
              });
            },
            icon: {
              default: { icon: "fa-regular fa-arrow-right-arrow-left" },
              hover: { icon: "fa-solid fa-arrow-right-arrow-left" },
            },
          },
          {
            name: "set-default-client",
            action: () => {
              store.dispatch("modals/open", {
                template: UpdateDefaultClient,
              });
            },
            icon: {
              default: { icon: "fa-regular fa-slider" },
              hover: { icon: "fa-solid fa-slider" },
            },
          },
        ]);

        menuLinks = menuLinks.concat(loginableLinks);
      }

      const endLinks = createNavLinks([
        ...(client.value?.planCount > 0
          ? [
              {
                name: "feedback",
                route: "feedback",
                icon: {
                  default: { icon: "fa-regular fa-message-pen" },
                  hover: { icon: "fa-solid fa-message-pen" },
                },
              },
            ]
          : []),
        {
          name: "announcements",
          route: "announcements",
          icon: {
            default: { icon: "fa-regular fa-bell" },
            hover: { icon: "fa-solid fa-bell" },
          },
        },
        {
          name: "knowledge-base",
          route: constants.LINKS.NEXCESS.KB.HELP,
          icon: {
            default: { icon: "fa-regular fa-books" },
            hover: { icon: "fa-solid fa-books" },
          },
        },
        {
          name: "signout",
          action: logout,
          icon: {
            default: { icon: "fa-regular fa-circle-xmark" },
            hover: { icon: "fa-solid fa-circle-xmark" },
          },
          variant: "danger",
        },
      ]);

      menuLinks = menuLinks.concat(endLinks);

      return menuLinks;
    });

    const createNavLinks = (links) =>
      links.map(
        (link) =>
          new NavLink(
            navLinkNameMapper(link.name, link.meta),
            link.icon,
            link.action || link.route,
            false,
            link.variant || "default",
            null,
            false,
            false,
            null,
            null,
            null,
            link.slot
          )
      );

    const logout = () => {
      store.dispatch("modals/open", {
        template: Logout,
      });
    };

    return {
      client,
      // enableSneakPeek,
      helpLinks,
      hideSidebar,
      isAdmin,
      isLoginPage,
      isNxToLwBannerDismissed,
      menu,
      showNav,
      user,
      userLoginableItems,
      userMenuItems,
      userMenuItemsMobile,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";

.outer {
  display: flex;
  flex-direction: column;
  height: 100%;
  .adminlogin,
  .sneakPeek {
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 0.875rem;
  }

  .layout-leftnav {
    flex: 1;
    display: flex;
    flex-direction: row;
    // height: 100%;
    overflow: auto;
    min-height: 0;

    .right-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow-x: hidden;

      .content {
        // padding: $content-top-padding $grid-gutter-width;
        overflow: auto;
        flex-grow: 1;
      }
    }
    .header,
    .header-cart {
      // height: $header-height;
      flex-grow: 0;
    }

    .loader {
      height: 100%;
      width: 100%;
    }
  }
}
.top-nav-bar {
  z-index: 1000;
}
</style>
